import type { ButtonHTMLAttributes, AnchorHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'inverse'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface BaseProps {
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
}

export function buttonClasses({
  variant = 'primary',
  size = 'md',
  className,
}: BaseProps = {}): string {
  // MOW-style: chunky, uppercase, slight rounded corners, weighty.
  const base =
    'inline-flex items-center justify-center gap-2 font-display font-extrabold uppercase tracking-[0.06em] ' +
    'rounded-sm border-2 transition-[background-color,color,border-color] duration-200 ease-out-quart ' +
    'disabled:opacity-50 disabled:cursor-not-allowed ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface'

  const sizes: Record<ButtonSize, string> = {
    sm: 'px-4 py-2 text-[0.75rem] leading-none',
    md: 'px-5 py-3 text-[0.8125rem] leading-none',
    lg: 'px-7 py-4 text-[0.875rem] leading-none',
  }

  const variants: Record<ButtonVariant, string> = {
    primary:
      'border-brand-strong bg-brand-strong text-surface hover:bg-brand-hover hover:border-brand-hover',
    secondary:
      'border-ink-deep bg-transparent text-ink-deep hover:bg-ink-deep hover:text-surface',
    ghost:
      'border-transparent bg-transparent text-ink-deep hover:bg-surface-alt',
    inverse:
      'border-surface bg-surface text-ink-deep hover:bg-transparent hover:text-surface',
  }

  return cn(base, sizes[size], variants[variant], className)
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & BaseProps

export function Button({
  variant,
  size,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClasses({ variant, size, className })}
      {...rest}
    />
  )
}

type ButtonLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & BaseProps

export function ButtonLink({
  variant,
  size,
  className,
  ...rest
}: ButtonLinkProps) {
  return (
    <a className={buttonClasses({ variant, size, className })} {...rest} />
  )
}
